import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Ticket, Download, Calendar, MapPin, QrCode, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

import { API } from "../config/api";

export default function MyTicketsPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthAndFetch();
  }, []);

  const checkAuthAndFetch = async () => {
    const token = localStorage.getItem("member_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const [ticketsRes, ordersRes] = await Promise.all([
        fetch(`${API}/member/tickets`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/member/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (ticketsRes.ok) {
        setTickets(await ticketsRes.json());
      }
      if (ordersRes.ok) {
        setOrders(await ordersRes.json());
      }
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  const downloadTicket = async (ticketId) => {
    const token = localStorage.getItem("member_token");
    try {
      const response = await fetch(`${API}/member/tickets/${ticketId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ticket_${ticketId.slice(0, 8)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Ticket downloaded!");
      } else {
        toast.error("Failed to download ticket");
      }
    } catch (error) {
      toast.error("Download failed");
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-kerala-cream flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-kerala-cream" data-testid="my-tickets-page">
      {/* Hero */}
      <section className="py-12 bg-kerala-green">
        <div className="section-container">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/member">
              <Button variant="ghost" className="text-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-kerala-gold rounded-full flex items-center justify-center">
              <Ticket className="w-7 h-7 text-kerala-green" />
            </div>
            <div>
              <h1 className="font-heading text-3xl text-white">My Tickets</h1>
              <p className="text-white/70">View and download your purchased tickets</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tickets List */}
      <section className="section-padding">
        <div className="section-container">
          {tickets.length === 0 ? (
            <div className="bg-white rounded-sm shadow-lg p-12 text-center">
              <Ticket className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="font-heading text-2xl text-kerala-green mb-2">No Tickets Yet</h2>
              <p className="text-gray-600 mb-6">
                You haven't purchased any tickets yet. Book your tickets for the Annual Picnic 2026!
              </p>
              <Link to="/picnic-2026">
                <Button className="btn-primary">Book Tickets</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-white rounded-sm shadow p-6">
                <h2 className="font-heading text-xl text-kerala-green mb-4">
                  You have {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                </h2>
                <p className="text-gray-600">
                  Click on each ticket to download the PDF. Present your ticket at the event entrance.
                </p>
              </div>

              {/* Tickets Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    className="bg-white rounded-sm shadow-lg overflow-hidden"
                    data-testid={`ticket-${ticket.ticket_id}`}
                  >
                    {/* Ticket Header */}
                    <div className="bg-kerala-green p-4 text-white">
                      <p className="text-kerala-gold text-xs tracking-widest uppercase mb-1">
                        TATVAMASI
                      </p>
                      <h3 className="font-heading text-lg">
                        {ticket.event_name || "Annual Picnic 2026"}
                      </h3>
                    </div>

                    {/* Ticket Body */}
                    <div className="p-4 space-y-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4 text-kerala-gold" />
                        <span className="text-sm">{ticket.event_date || "July 11, 2026"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4 text-kerala-gold" />
                        <span className="text-sm">London, Ontario</span>
                      </div>
                      
                      <div className="pt-3 border-t border-kerala-gold/10">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-500">Ticket Type</span>
                          <span className="font-medium text-kerala-green">
                            {ticket.ticket_type || "General"}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">Ticket ID</span>
                          <span className="font-mono text-sm text-kerala-green">
                            {ticket.ticket_id?.slice(0, 8).toUpperCase()}
                          </span>
                        </div>
                      </div>

                      {/* QR indicator */}
                      <div className="flex items-center gap-2 pt-3 border-t border-kerala-gold/10">
                        <QrCode className="w-4 h-4 text-kerala-green" />
                        <span className="text-xs text-gray-500">QR code included in PDF</span>
                      </div>
                    </div>

                    {/* Download Button */}
                    <div className="p-4 bg-kerala-cream/50">
                      <Button
                        className="btn-primary w-full"
                        onClick={() => downloadTicket(ticket.ticket_id)}
                        data-testid={`download-ticket-${ticket.ticket_id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Ticket
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Orders Summary */}
          {orders.length > 0 && (
            <div className="mt-12">
              <h2 className="font-heading text-2xl text-kerala-green mb-6">Order History</h2>
              <div className="bg-white rounded-sm shadow overflow-hidden">
                <table className="w-full">
                  <thead className="bg-kerala-green text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">Order ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-kerala-gold/10">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-kerala-cream/30">
                        <td className="px-4 py-3 font-mono text-sm">
                          {order.id?.slice(0, 8).toUpperCase()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {order.paid_at 
                            ? new Date(order.paid_at).toLocaleDateString()
                            : new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-kerala-green">
                          ${((order.amount_cents || 0) / 100).toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
